import {
  organizationDetailSchema,
  SSO_USER_POLICIES,
  SSO_USER_POLICY_LABELS,
  ssoConfigInputSchema,
  ssoConfigResponseSchema,
  ssoTestResultSchema,
  userFormOptionsSchema,
  type SsoConfig,
  type SsoConfigResponse,
  type SsoTestResult,
  type SsoUserPolicy,
} from '@fluxus/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, KeyRound, PlugZap, Trash2 } from 'lucide-react';
import { useEffect, useId, useMemo, useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router';
import { toast } from 'sonner';
import { PageContainer, PageHeader } from '@/components/layout/PageHeader';
import { EmptyState, ErrorState, LoadingState } from '@/components/states/States';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { FormAlert, FormField, PasswordInput, fieldA11y } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { NativeSelect } from '@/components/ui/select';
import { CompanyNav } from '@/features/organizations/CompanyNav';
import { userOptionsKey } from '@/features/users/UserForm';
import { errorMessage } from '@/lib/api/errors';
import { useAuth, useCan } from '@/lib/auth/session';
import { apiFieldErrors, hasFieldErrors, zodFieldErrors, type FieldErrors } from '@/lib/forms';

const ssoKey = (organizationId: string) => ['sso', organizationId];

/** Domínios digitados separados por vírgula, espaço ou linha. */
const splitDomains = (text: string) =>
  text
    .split(/[\s,;]+/)
    .map((d) => d.trim())
    .filter(Boolean);

/**
 * Login único (SSO) da empresa cliente (T-028c, RF-04; DEC-072): a equipe Fluxus configura o provedor OpenID Connect
 * (emissor, client id e secret), os domínios de e-mail atendidos e quem pode entrar; testa a conexão antes de ativar.
 */
export function SsoPage() {
  const { organizationId = '' } = useParams();
  const { client } = useAuth();
  const can = useCan();
  const org = useQuery({
    queryKey: ['organizations', 'detail', organizationId],
    queryFn: ({ signal }) =>
      client.request(`/organizations/${organizationId}`, { schema: organizationDetailSchema, signal }),
  });
  const sso = useQuery({
    queryKey: ssoKey(organizationId),
    queryFn: ({ signal }) =>
      client.request(`/organizations/${organizationId}/sso`, { schema: ssoConfigResponseSchema, signal }),
  });
  const [creating, setCreating] = useState(false);
  const canEdit = can('administracao', 'edit');

  return (
    <PageContainer className="max-w-5xl">
      <Link
        to={`/empresas/${organizationId}`}
        className="inline-flex items-center gap-1.5 text-sm font-semibold text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="size-4" aria-hidden />
        {org.data?.name ?? 'Empresa'}
      </Link>
      <PageHeader
        className="mt-4"
        title="Login único (SSO)"
        description="Quem tem e-mail dos domínios da empresa entra com a conta corporativa, pelo provedor OpenID Connect dela (que informe o e-mail confirmado da pessoa, como Google e Keycloak)."
      />
      <CompanyNav organizationId={organizationId} />
      <div className="mt-6 flex flex-col gap-6">
        {sso.data ? (
          <p className="rounded-md border border-border bg-surface p-4 text-sm">
            <span className="font-semibold">
              Endereço de retorno para cadastrar no provedor (redirect URI):{' '}
            </span>
            <code className="break-all">{sso.data.callbackUrl}</code>
          </p>
        ) : null}
        {sso.isPending ? (
          <LoadingState rows={4} label="Carregando o SSO da empresa" />
        ) : sso.isError ? (
          <ErrorState error={sso.error} onRetry={() => void sso.refetch()} retrying={sso.isFetching} />
        ) : sso.data.config || creating ? (
          <SsoForm
            key={sso.data.config?.updatedAt ?? 'novo'}
            organizationId={organizationId}
            config={sso.data.config}
            canEdit={canEdit}
            onDone={() => setCreating(false)}
          />
        ) : (
          <EmptyState
            icon={KeyRound}
            title="SSO não configurado"
            description="As pessoas da empresa entram com e-mail e senha. Configure o provedor da empresa para elas entrarem com a conta corporativa."
            action={canEdit ? <Button onClick={() => setCreating(true)}>Configurar SSO</Button> : undefined}
          />
        )}
      </div>
    </PageContainer>
  );
}

function SsoForm({
  organizationId,
  config,
  canEdit,
  onDone,
}: {
  organizationId: string;
  config: SsoConfig | null;
  canEdit: boolean;
  /** Fim do "configurar do zero" (cancelado, salvo ou removido): a página volta a seguir só a API. */
  onDone: () => void;
}) {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  const [enabled, setEnabled] = useState(config?.enabled ?? false);
  const [issuer, setIssuer] = useState(config?.issuer ?? '');
  const [clientId, setClientId] = useState(config?.clientId ?? '');
  const [clientSecret, setClientSecret] = useState('');
  const [domains, setDomains] = useState(config?.domains.join(', ') ?? '');
  const [userPolicy, setUserPolicy] = useState<SsoUserPolicy>(config?.userPolicy ?? 'EXISTING_ONLY');
  const [defaultRoleId, setDefaultRoleId] = useState(config?.defaultRole?.id ?? '');
  const [errors, setErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<unknown>(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [test, setTest] = useState<{ ok: SsoTestResult } | { error: unknown } | null>(null);
  const [removing, setRemoving] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  /** Campo que recebe o foco depois de renderizar (os campos ficam desabilitados enquanto salva). */
  const [focusField, setFocusField] = useState<keyof typeof ids | null>(null);
  const base = useId();
  const ids = useMemo(
    () => ({
      enabled: `${base}-ativo`,
      issuer: `${base}-emissor`,
      clientId: `${base}-client-id`,
      secret: `${base}-secret`,
      domains: `${base}-dominios`,
      policy: `${base}-politica`,
      role: `${base}-perfil`,
      alert: `${base}-alerta`,
    }),
    [base],
  );

  const roles = useQuery({
    queryKey: userOptionsKey(organizationId),
    queryFn: ({ signal }) =>
      client.request(`/organizations/${organizationId}/users/options`, {
        schema: userFormOptionsSchema,
        signal,
      }),
    enabled: canEdit,
  });

  const clear = (field: string) => setErrors((e) => ({ ...e, [field]: undefined }));
  useEffect(() => {
    if (!focusField || saving) return;
    document.getElementById(ids[focusField])?.focus();
    setFocusField(null);
  }, [focusField, saving, ids]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (saving) return;
    setFormError(null);
    const body = {
      enabled,
      issuer,
      clientId,
      clientSecret,
      domains: splitDomains(domains),
      userPolicy,
      defaultRoleId: userPolicy === 'CREATE' ? defaultRoleId || null : null,
    };
    const parsed = ssoConfigInputSchema.safeParse(body);
    const found = parsed.success ? {} : zodFieldErrors(parsed.error);
    if (!config && !clientSecret.trim()) found.clientSecret = 'Informe o client secret.';
    if (hasFieldErrors(found)) {
      setErrors(found);
      setFocusField(fieldId(found));
      return;
    }
    setErrors({});
    setSaving(true);
    try {
      const saved = await client.request(`/organizations/${organizationId}/sso`, {
        method: 'PUT',
        body,
        schema: ssoConfigResponseSchema,
      });
      queryClient.setQueryData(ssoKey(organizationId), saved);
      onDone();
      toast.success(config ? 'SSO salvo' : 'SSO configurado');
    } catch (err) {
      const fields = apiFieldErrors(err);
      if (hasFieldErrors(fields)) {
        setErrors(fields);
        setFocusField(fieldId(fields));
      } else setFormError(err);
    } finally {
      setSaving(false);
    }
  }

  async function runTest() {
    const parsed = ssoConfigInputSchema.shape.issuer.safeParse(issuer);
    if (!parsed.success) {
      setErrors((e) => ({ ...e, issuer: parsed.error.issues[0]?.message }));
      setFocusField('issuer');
      return;
    }
    setTesting(true);
    setTest(null);
    try {
      const ok = await client.request(`/organizations/${organizationId}/sso/test`, {
        method: 'POST',
        body: { issuer },
        schema: ssoTestResultSchema,
      });
      setTest({ ok });
    } catch (error) {
      setTest({ error });
    } finally {
      setTesting(false);
    }
  }

  async function remove() {
    setRemoving(true);
    try {
      await client.request(`/organizations/${organizationId}/sso`, { method: 'DELETE' });
      queryClient.setQueryData<SsoConfigResponse>(ssoKey(organizationId), (prev) =>
        prev ? { ...prev, config: null } : prev,
      );
      onDone();
      setConfirmRemove(false);
      toast.success('SSO removido');
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setRemoving(false);
    }
  }

  return (
    <form
      noValidate
      onSubmit={submit}
      aria-label="Configuração do SSO"
      aria-describedby={formError ? ids.alert : undefined}
      className="flex flex-col gap-6 rounded-lg border border-border bg-surface p-5"
    >
      <fieldset disabled={!canEdit || saving} className="flex flex-col gap-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <label className="inline-flex items-center gap-2 text-sm font-semibold">
            <input
              id={ids.enabled}
              type="checkbox"
              className="size-4 accent-(--brand)"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
            />
            SSO ativo (as pessoas dos domínios podem entrar pelo provedor)
          </label>
          {config ? (
            <Badge variant={config.enabled ? 'brand' : 'neutral'}>
              {config.enabled ? 'Ativo' : 'Inativo'}
            </Badge>
          ) : null}
        </div>

        <div className="grid gap-5 md:grid-cols-2">
          <FormField
            id={ids.issuer}
            label="Emissor (issuer)"
            error={errors.issuer}
            hint={
              <p className="text-xs text-muted-foreground">
                Endereço do provedor, ex.: https://login.microsoftonline.com/&lt;tenant&gt;/v2.0
              </p>
            }
          >
            <Input
              {...fieldA11y(ids.issuer, errors.issuer, true)}
              type="url"
              inputMode="url"
              autoCapitalize="none"
              spellCheck={false}
              maxLength={512}
              value={issuer}
              onChange={(e) => {
                setIssuer(e.target.value);
                clear('issuer');
                setTest(null);
              }}
            />
          </FormField>
          <FormField id={ids.clientId} label="Client id" error={errors.clientId}>
            <Input
              {...fieldA11y(ids.clientId, errors.clientId)}
              autoCapitalize="none"
              spellCheck={false}
              maxLength={255}
              value={clientId}
              onChange={(e) => {
                setClientId(e.target.value);
                clear('clientId');
              }}
            />
          </FormField>
          <FormField
            id={ids.secret}
            label="Client secret"
            error={errors.clientSecret}
            hint={
              <p className="text-xs text-muted-foreground">
                {config?.clientSecretSet
                  ? 'Guardado com segurança e nunca exibido. Deixe em branco para manter o atual.'
                  : 'Guardado criptografado; depois de salvo, não é exibido de novo.'}
              </p>
            }
          >
            <PasswordInput
              {...fieldA11y(ids.secret, errors.clientSecret, true)}
              autoComplete="new-password"
              maxLength={512}
              value={clientSecret}
              onChange={(e) => {
                setClientSecret(e.target.value);
                clear('clientSecret');
              }}
            />
          </FormField>
          <FormField
            id={ids.domains}
            label="Domínios de e-mail"
            error={errors.domains}
            hint={
              <p className="text-xs text-muted-foreground">
                Separe por vírgula, ex.: empresa.com.br, filial.com. Cada domínio pode estar no SSO de uma só
                empresa.
              </p>
            }
          >
            <Input
              {...fieldA11y(ids.domains, errors.domains, true)}
              autoCapitalize="none"
              spellCheck={false}
              value={domains}
              onChange={(e) => {
                setDomains(e.target.value);
                clear('domains');
              }}
            />
          </FormField>
        </div>

        <fieldset className="flex flex-col gap-2">
          <legend className="mb-1 text-sm font-semibold">Quem pode entrar pelo SSO</legend>
          {SSO_USER_POLICIES.map((p) => (
            <label key={p} className="inline-flex items-start gap-2 text-sm">
              <input
                type="radio"
                name={ids.policy}
                className="mt-0.5 size-4 accent-(--brand)"
                checked={userPolicy === p}
                onChange={() => {
                  setUserPolicy(p);
                  clear('defaultRoleId');
                }}
              />
              {SSO_USER_POLICY_LABELS[p]}
            </label>
          ))}
        </fieldset>

        {userPolicy === 'CREATE' ? (
          <FormField id={ids.role} label="Perfil de quem for criado" error={errors.defaultRoleId}>
            {roles.isError ? (
              <FormAlert tone="error">
                Não foi possível carregar os perfis.{' '}
                <button
                  type="button"
                  className="font-semibold underline"
                  onClick={() => void roles.refetch()}
                >
                  Tentar novamente
                </button>
              </FormAlert>
            ) : (
              <NativeSelect
                {...fieldA11y(ids.role, errors.defaultRoleId)}
                value={defaultRoleId}
                disabled={roles.isPending && canEdit}
                onChange={(e) => {
                  setDefaultRoleId(e.target.value);
                  clear('defaultRoleId');
                }}
              >
                <option value="">
                  {roles.isPending && canEdit ? 'Carregando perfis…' : 'Escolha o perfil'}
                </option>
                {(roles.data?.roles ?? (config?.defaultRole ? [config.defaultRole] : [])).map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name}
                  </option>
                ))}
              </NativeSelect>
            )}
          </FormField>
        ) : null}
      </fieldset>

      {test ? (
        'ok' in test ? (
          <FormAlert tone="success" title="Conexão com o provedor funcionando">
            <ul className="mt-1 flex flex-col gap-0.5 break-all">
              <li>Autorização: {test.ok.authorizationEndpoint}</li>
              <li>Troca do código: {test.ok.tokenEndpoint}</li>
              <li>
                {test.ok.signingKeys} {test.ok.signingKeys === 1 ? 'chave' : 'chaves'} de assinatura publicada
                {test.ok.signingKeys === 1 ? '' : 's'}
              </li>
            </ul>
          </FormAlert>
        ) : (
          <FormAlert tone="error" title="A conexão com o provedor falhou">
            {errorMessage(test.error)}
          </FormAlert>
        )
      ) : null}

      {formError ? (
        <FormAlert id={ids.alert} tone="error">
          {errorMessage(formError)}
        </FormAlert>
      ) : null}

      {canEdit ? (
        <div className="flex flex-wrap items-center gap-3">
          <Button type="submit" loading={saving} disabled={removing}>
            {config ? 'Salvar SSO' : 'Configurar SSO'}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => void runTest()}
            loading={testing}
            disabled={saving || removing}
          >
            <PlugZap aria-hidden />
            Testar conexão
          </Button>
          {config ? (
            <Button
              type="button"
              variant="ghost"
              className="sm:ml-auto"
              disabled={saving || testing}
              onClick={() => setConfirmRemove(true)}
            >
              <Trash2 aria-hidden />
              Remover SSO
            </Button>
          ) : (
            <Button type="button" variant="ghost" disabled={saving} onClick={onDone}>
              Cancelar
            </Button>
          )}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">Seu perfil só pode consultar o SSO desta empresa.</p>
      )}

      <ConfirmDialog
        open={confirmRemove}
        onOpenChange={setConfirmRemove}
        title="Remover o SSO da empresa?"
        description="As pessoas voltam a entrar só com e-mail e senha, e os domínios ficam livres. O client secret guardado é apagado."
        confirmLabel="Remover SSO"
        destructive
        busy={removing}
        onConfirm={() => void remove()}
      />
    </form>
  );
}

/** Primeiro campo com erro, na ordem da tela (para levar o foco até ele). */
function fieldId(errors: FieldErrors): 'issuer' | 'clientId' | 'secret' | 'domains' | 'role' {
  if (errors.issuer) return 'issuer';
  if (errors.clientId) return 'clientId';
  if (errors.clientSecret) return 'secret';
  if (errors.domains) return 'domains';
  return 'role';
}
